/**
 * @component Header
 * @description 회사 홈페이지 전역 네비게이션(데스크톱 드롭다운/모바일 아코디언)을 제공하는 헤더 컴포넌트입니다.
 * @usage MainLayout 상단에 고정 배치되어 모든 페이지 이동 진입점을 제공합니다.
 */

import { navItems } from '../../constants/navigation';
import { useHeaderState } from '../../hooks/useHeaderState';

export default function Header({ pathname, onNavigate }) {
  // [상태 조회]: 헤더 활성 메뉴, 스크롤/모바일 열림 상태를 헤더 전용 훅에서 가져옵니다.
  const {
    activeSubLevel,
    activeTopLevel,
    headerClassName,
    isSolid,
    mobileOpen,
    openMenu,
    setIsHovered,
    setMobileOpen,
    setOpenMenu,
  } = useHeaderState(pathname);

  // [기능] 햄버거 메뉴 토글 핸들러. 메뉴를 닫을 때 호버 상태도 함께 리셋합니다.
  // [비즈니스] 터치 기기에서는 mouseleave가 발생하지 않아 isHovered가 true로 남게 되는데,
  // 이 경우 메뉴를 닫아도 헤더가 흰색으로 유지되는 문제가 발생하므로 닫는 시점에 강제 리셋합니다.
  const handleMobileToggle = () => {
    setMobileOpen((current) => {
      if (current) {
        setIsHovered(false);
      }
      return !current;
    });
  };

  // [비즈니스] 헤더가 투명 상태일 때는 흰색 로고, 불투명 상태일 때는 컬러 로고를 표시합니다.
  const logoSrc = isSolid ? '/saehim_logo.png' : '/saehim_logo_white.png';

  return (
    <header
      className={headerClassName}
      // [이벤트 처리]: 마우스가 있는 기기에서만 호버로 헤더 배경을 불투명으로 전환합니다.
      // [비즈니스] 터치 기기는 synthetic mouseenter는 발생해도 mouseleave가 안정적으로 발생하지 않아
      // isHovered 상태가 true로 고착되어 햄버거 메뉴를 닫아도 헤더가 투명으로 돌아오지 않는 문제가 있습니다.
      onMouseEnter={() => {
        if (window.matchMedia('(hover: none)').matches) return;
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        if (window.matchMedia('(hover: none)').matches) return;
        setIsHovered(false);
      }}
    >
      <div className="container header-inner">
        <button type="button" className="brand" onClick={() => onNavigate('/')}>
          <img className="brand-logo" src={logoSrc} alt="새힘정보기술 로고" />
        </button>

        <button
          type="button"
          className={`mobile-nav-toggle ${mobileOpen ? 'is-open' : ''}`}
          // [이벤트 처리]: 모바일 햄버거 버튼 클릭 시 메뉴 패널 열림 상태를 토글합니다.
          onClick={handleMobileToggle}
          aria-label="모바일 메뉴 열기"
          aria-expanded={mobileOpen}
        >
          <span />
          <span />
          <span />
        </button>

        <nav className={`gnb ${mobileOpen ? 'is-open' : ''}`} aria-label="주요 메뉴">
          {navItems.map((item) => {
            const hasChildren = Array.isArray(item.children);
            const isOpen = openMenu === item.id;

            const handleTopLevelClick = () => {
              // [비즈니스] 모바일 레이아웃 판정: 1024px 이하이거나 hover 불가능한 터치 기기.
              // hover: none 조건을 함께 검사하는 이유는 가로 모드 iPad(예: 1180px)처럼
              // 너비는 데스크톱 범위지만 마우스가 없는 기기에서도 클릭 기반 아코디언이 동작해야 하기 때문입니다.
              // CSS 미디어 쿼리(@media (max-width: 1024px), (hover: none))와 동일한 기준입니다.
              const isMobileLayout =
                window.innerWidth <= 1024 || window.matchMedia('(hover: none)').matches;

              // [조건 분기]: 모바일 레이아웃에서 하위 메뉴가 있으면 아코디언 토글, 그 외에는 페이지 이동합니다.
              if (hasChildren && isMobileLayout) {
                setOpenMenu((current) => (current === item.id ? '' : item.id));
                return;
              }

              // [이벤트 처리]: 데스크톱(마우스 사용) 환경에서 클릭 시 해당 경로로 이동합니다.
              onNavigate(item.href);
            };

            return (
              <div
                key={item.id}
                className={`gnb-item ${hasChildren ? 'gnb-submenu' : ''} ${isOpen ? 'is-open' : ''}`}
                onMouseEnter={() => {
                  // [비즈니스] 데스크톱(마우스 사용)에서만 hover로 드롭다운을 펼칩니다.
                  // 1024px 초과이면서 hover가 가능한 기기에서만 동작하여 터치 기기 오작동을 방지합니다.
                  if (!hasChildren) return;
                  if (window.innerWidth <= 1024) return;
                  if (window.matchMedia('(hover: none)').matches) return;
                  setOpenMenu(item.id);
                }}
                onMouseLeave={() => {
                  if (!hasChildren) return;
                  if (window.innerWidth <= 1024) return;
                  if (window.matchMedia('(hover: none)').matches) return;
                  setOpenMenu('');
                }}
              >
                <button
                  type="button"
                  className={`gnb-link ${hasChildren ? 'has-children' : ''} ${activeTopLevel === item.id ? 'is-active' : ''}`}
                  onClick={handleTopLevelClick}
                  aria-haspopup={hasChildren ? 'menu' : undefined}
                  aria-expanded={hasChildren ? isOpen : undefined}
                >
                  {item.label}
                  {hasChildren ? <span className={`gnb-caret ${isOpen ? 'is-open' : ''}`} aria-hidden="true" /> : null}
                </button>

                {hasChildren ? (
                  <>
                    <div className="submenu-bridge" aria-hidden="true" />
                    <div className="submenu-panel">
                      {item.children.map((child) => (
                        <button
                          key={child.id}
                          type="button"
                          className={`submenu-link ${activeSubLevel === child.id ? 'is-active' : ''}`}
                          onClick={() => onNavigate(child.href)}
                        >
                          {child.label}
                        </button>
                      ))}
                    </div>

                    <div className="mobile-submenu">
                      {item.children.map((child) => (
                        <button
                          key={`${item.id}-${child.id}`}
                          type="button"
                          className={`mobile-submenu-link ${activeSubLevel === child.id ? 'is-active' : ''}`}
                          onClick={() => onNavigate(child.href)}
                        >
                          {child.label}
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            );
          })}
        </nav>
      </div>
    </header>
  );
}
